import React, { Fragment, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import OwnershipKpiStrip from "./components/OwnershipKpiStrip.jsx";
import SourceList from "./components/SourceList.jsx";
import RuleWizardDrawer from "./components/RuleWizardDrawer.jsx";
import UncoveredCostPanel from "./components/UncoveredCostPanel.jsx";
import { getMatrix } from "./api";
import { getBudgetDimensions } from "../cost-allocation/api/costModelApi";
import { resolveDimensions, orgDimensionId } from "./dimensions";

const FinancialOwnershipPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [pendingCell, setPendingCell] = useState(null);

  // Resolve the real dimension ids (and the organisation id) from the backend, by code.
  const { data: dimensionsRaw } = useQuery(["budget-dimensions"], getBudgetDimensions);
  const DIMENSIONS = React.useMemo(() => resolveDimensions(dimensionsRaw), [dimensionsRaw]);
  const orgDimId = React.useMemo(() => orgDimensionId(DIMENSIONS), [DIMENSIONS]);

  const handleCellClick = (source, dimensionId) => {
    const dimension = DIMENSIONS.find((d) => d.id === dimensionId) || { id: dimensionId, name: String(dimensionId) };
    setPendingCell({ source, dimension });
  };

  // Fetch all sources once; grouping (by source type) and filtering happen client-side
  // in the matrix from the shared store, so no refetch is needed when those controls change.
  const { data, isLoading, error } = useQuery(
    ["alloc-matrix"],
    () => getMatrix(),
    { keepPreviousData: true }
  );

  const sources = React.useMemo(() => data?.sources || [], [data]);
  const coverage = React.useMemo(() => data?.coverage || [], [data]);

  // High-confidence share comes from the backend's cost-row outcome (the engine knows what
  // actually matched in the past period — the matrix config cannot derive it client-side).
  const highConfPct =
    coverage.length > 0
      ? coverage.reduce((sum, r) => sum + (r.high_conf_pct || 0), 0) / coverage.length
      : 0;

  // "Unallocated" splits into two non-overlapping buckets, both measured on the
  // organisational dimension (the primary ownership lens):
  //   1. no-owner   — cost that a source DOES match, but which has no organisational owner
  //                   rule. Derived live from the matrix so it always agrees with the grid.
  //   2. not-covered — cost that matches NO source at all. This never appears in the matrix
  //                   (sources only hold matched cost), so it comes from the backend's
  //                   uncoveredCost. Normally 0 because the catch-all "Other" source mops up
  //                   the residual; a non-zero value means cost is escaping every source.
  const noOwnerCost = React.useMemo(
    () =>
      sources.reduce(
        (sum, s) => sum + (s.cells?.[orgDimId] ? 0 : s.monthlyCost || 0),
        0
      ),
    [sources, orgDimId]
  );
  const notCoveredCost = data?.uncoveredCost || 0;
  // Top contributors to the uncovered cost (by service) + the real total, for the guided-
  // remediation strip/drawer. Total = covered (sum of matched source cost) + not-covered.
  const uncoveredBreakdown = React.useMemo(() => data?.uncoveredBreakdown || [], [data]);
  const totalCost = React.useMemo(
    () => sources.reduce((sum, s) => sum + (s.monthlyCost || 0), 0) + notCoveredCost,
    [sources, notCoveredCost]
  );

  // Per-day unallocated trend for the sparkline — organisational dimension only, so the
  // chart tracks the same metric the headline shows. Sorted ascending (left = oldest).
  const unallocatedTrend = React.useMemo(() => {
    const byDay = new Map();
    for (const r of coverage) {
      if (!r.day || r.dimension_id !== orgDimId) continue;
      const day = String(r.day).slice(0, 10);
      byDay.set(day, (byDay.get(day) || 0) + (r.unallocated_cost || 0));
    }
    return Array.from(byDay.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, value]) => ({ day, value }));
  }, [coverage, orgDimId]);

  const handleDrawerSaved = () => {
    setPendingCell(null);
    queryClient.invalidateQueries(["alloc-matrix"]);
  };

  // Deep-link focus: the Ownership dashboard sends users here with ?focus=no-owner (cost in a
  // source with no org owner — fix in the matrix) or ?focus=uncovered (cost no source matches —
  // fix via the uncovered panel). Scroll to the relevant section and flash it so the user lands
  // exactly on the items the dashboard promised, not a generic page dump.
  const [searchParams] = useSearchParams();
  const focus = searchParams.get("focus");
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (!focus || isLoading || error) return undefined;
    let flashTimer;
    const t = setTimeout(() => {
      const el = document.getElementById(focus === "uncovered" ? "uncovered-focus" : "no-owner-focus");
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: focus === "uncovered" ? "center" : "start" });
      setFlash(true);
      flashTimer = setTimeout(() => setFlash(false), 1800);
    }, 100);
    return () => {
      clearTimeout(t);
      clearTimeout(flashTimer);
    };
  }, [focus, isLoading, error]);

  return (
    <Fragment>
      {/* showPeriodSelector={false}: this page is configuration-centric — rules are
          long-lived and the matrix structure doesn't change with the date range. The
          KPIs are computed as a "current state" snapshot from the backend. A period
          chooser at the top suggested the matrix was filtered by time, which it wasn't,
          which was confusing finance users. */}
      <AdministrationHeader title="Financial Ownership" className="text-text-3" showPeriodSelector={false} />

      <div className="text-left flex flex-auto flex-col ml-10 mt-6 mr-6">
        {/* No in-body title/description — the page leads straight with the metrics, matching
            the sibling Economic Control pages. */}
        {/* KPI strip — replaces the previous green banner. Cards instead of a strip so
            each metric gets equal visual weight and the contradictory "100% allocated /
            €42k unallocated" framing can no longer happen. */}
        <OwnershipKpiStrip
          sources={sources}
          dimensions={DIMENSIONS}
          highConfPct={highConfPct}
          noOwnerCost={noOwnerCost}
          notCoveredCost={notCoveredCost}
          primaryDimension={{ id: orgDimId, name: "Organisation" }}
          unallocatedTrend={unallocatedTrend}
        />

        {/* Uncovered-cost alert (when present) sits below the KPIs and above the matrix. The
            primary "New source" action now lives in the matrix table's header. */}
        <div
          id="uncovered-focus"
          className={`mt-4 mb-2 min-w-0 rounded-card transition-shadow ${
            focus === "uncovered" && flash ? "ring-2 ring-warning ring-offset-2" : ""
          }`}
        >
          {!isLoading && !error && (
            <UncoveredCostPanel
              notCoveredCost={notCoveredCost}
              totalCost={totalCost}
              breakdown={uncoveredBreakdown}
              onAddSource={(service) =>
                navigate("/allocation/sources/new", {
                  state: { prefill: { dim: "service", values: [service], name: service } },
                })
              }
            />
          )}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-text-2" />
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-64 text-danger">
            Error loading allocation data
          </div>
        )}

        {!isLoading && !error && (
          <div
            id="no-owner-focus"
            className={`py-2 mb-2 mt-6 rounded-card flex flex-auto flex-col transition-shadow ${
              focus === "no-owner" && flash ? "ring-2 ring-warning" : ""
            }`}
          >
            <SourceList sources={sources} dimensions={DIMENSIONS} onCellClick={handleCellClick} />
          </div>
        )}
      </div>

      <RuleWizardDrawer
        open={pendingCell !== null}
        source={pendingCell?.source}
        dimension={pendingCell?.dimension}
        onClose={() => setPendingCell(null)}
        onSaved={handleDrawerSaved}
      />
    </Fragment>
  );
};

export default FinancialOwnershipPage;
